import type { PluginDefinition, PluginGroup } from '@/plugins/types';
import { ToolCard } from './ToolCard';
import { useRecentToolsStore } from '@/stores/useRecentToolsStore';
import { isReadOnlyAccess } from '@/auth/roleUtils';
import { useOrgMemberStore } from '@/stores/useOrgMemberStore';

const CATEGORY_META: Record<Exclude<PluginGroup, 'core'>, { title: string; description: string }> = {
  analysis: {
    title: 'Wissen & Analyse',
    description: 'Fragen stellen, Wissen erfassen und Zusammenhänge explorieren',
  },
  data: {
    title: 'Datenmanagement',
    description: 'Dokumente verwalten, synchronisieren und kuratieren',
  },
  admin: {
    title: 'Administration',
    description: 'System überwachen, konfigurieren und steuern',
  },
};

interface CategorySectionProps {
  group: Exclude<PluginGroup, 'core'>;
  plugins: PluginDefinition[];
}

export function CategorySection({ group, plugins }: CategorySectionProps) {
  const { trackVisit } = useRecentToolsStore();
  const { currentUser } = useOrgMemberStore();
  const orgRole = currentUser?.role ?? null;
  const meta = CATEGORY_META[group];

  if (plugins.length === 0) return null;

  return (
    <section data-testid={`category-${group}`}>
      <div className="mb-3">
        <h2 className="text-sm font-semibold">{meta.title}</h2>
        <p className="text-xs text-muted-foreground">{meta.description}</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {plugins.map((plugin) => {
          const nav = plugin.navigation?.[0];
          if (!nav) return null;
          return (
            <ToolCard
              key={plugin.id}
              id={plugin.id}
              name={plugin.name}
              description={plugin.description}
              icon={nav.icon}
              href={nav.path}
              group={group}
              isReadOnly={isReadOnlyAccess(plugin, orgRole)}
              onClick={() => trackVisit(plugin.id)}
            />
          );
        })}
      </div>
    </section>
  );
}
